import React, { useState, useEffect } from 'react';
import { Check, X, Mail, User, Phone } from 'lucide-react';
import { api } from '../../api/api';
import Loader from '../Loader';


const EnhancedUserForm = () => {

  const [loading, setLoading] = useState(false);
  // More sophisticated color palette with deeper, more mature tones
  const colors = {
    primary: "#364FC7", // Deeper indigo
    primaryDark: "#2B3A80", // Even darker indigo
    secondary: "#5E3B92", // Richer purple
    accent: "#D0C7E9", // Subtle light violet
    gradientStart: "#8B5CF6", // Deep blue-violet
    gradientEnd: "#6366F1", // Deep indigo
    background: "#F8F7FC", // Off-white with slight violet tint
    cardBg: "#FFFFFF",
    text: "#1A202C", // Deeper gray for better contrast
    textLight: "#64748B", // More sophisticated gray
    border: "#E2E8F0", // Refined border color
    error: "#DC2626", // Slightly darker red
    success: "#059669", // Deeper emerald
  };

  useEffect(() => {
    const fetchUserData = () => {
      setLoading(true);
      api.get('/users/checklogin', { withCredentials: true })
        .then((response) => {          
          api.post('/users/getUserData', { _id: response.data.user.user._id })
          .then((response) => {
            setLoading(false);
            setFormData({
              firstName: response.data.user.firstName,
              lastName: response.data.user.lastName,
              email: response.data.user.email,
              phoneNumber: response.data.user.phoneNumber
            });
          })
          .catch((error) => {
            console.log(error);
          });
        })
        .catch((error) => {
          console.log(error);
        });
    };
    fetchUserData();
  }, []);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    if (formData.phoneNumber && !/^0\d{9}$/.test(formData.phoneNumber)) {
      newErrors.phoneNumber = 'Phone number must be 10 digits starting with 0';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (isSuccess) setIsSuccess(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      setIsSubmitting(true);
      setLoading(true);
      api.get('/users/checklogin', { withCredentials: true }).then((response) => {
      api.post('/users/updateUser', {...formData, _id: response.data.user.user._id} )
        .then(() => {
          setLoading(false);
          setIsSubmitting(false);
          setIsSuccess(true);
        })
        .catch((error) => {
          setLoading(false);
          console.log(error);
          setIsSubmitting(false);
        });
      }).catch((error) => {
        setLoading(false);
        console.log(error);
      });
    }
  };

  // Icons for form fields
  const fieldIcons = {
    firstName: <User size={18} style={{ color: colors.textLight }} />,
    lastName: <User size={18} style={{ color: colors.textLight }} />,
    email: <Mail size={18} style={{ color: colors.textLight }} />,
    phoneNumber: <Phone size={18} style={{ color: colors.textLight }} />
  };

  return (
    <>
     {loading && <div className="absolute inset-0 bg-white z-10"> <Loader/> </div>}
    <div className=" w-full flex items-center justify-center max-w-6xl " >
      <div className="container mx-auto ">
        <div className="flex flex-col md:flex-row rounded-xl overflow-hidden shadow-xl " style={{ backgroundColor: colors.cardBg }}>
        {/* backgroundColor: colors.cardBg */}

          {/* Image Section */}
          <div className="hidden md:flex w-[50%] md:w-1/2 items-center justify-center p-8" style={{ backgroundColor: "#F5F7FF" }}>
            <div className="relative w-full h-full flex items-center justify-center">
              <img 
                src="./profile_img.png" 
                alt="Update Profile" 
                className="max-w-full max-h-full object-contain"
                style={{ filter: "drop-shadow(0 10px 15px rgba(59, 73, 137, 0.15))" }}
              />
            </div>
          </div>
          
          {/* Form Section */}
          <div className="w-full md:w-1/2 p-6 md:p-10 lg:p-12 relative">
            {/* Subtle decorative elements */}
            <div className="absolute top-0 right-0 w-40 h-40 rounded-full opacity-5" 
                 style={{ backgroundColor: colors.primary, transform: 'translate(25%, -25%)' }}></div>
            
        
            <form onSubmit={handleSubmit} className="space-y-5">
              {['firstName', 'lastName', 'email', 'phoneNumber'].map((field) => (
                <div key={field} className="mb-2">
                  <label className="block text-sm font-medium mb-2" 
                         style={{ color: colors.text, letterSpacing: "0.01em" }}>
                    {field === 'phoneNumber' 
                      ? <>Phone Number <span className="text-xs font-normal" style={{ color: colors.textLight }}></span></>
                      : field.replace(/([A-Z])/g, ' $1').trim().replace(/^\w/, c => c.toUpperCase())}
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      {fieldIcons[field]}
                    </div>
                    <input
                      type={field === 'email' ? 'email' : 'text'}
                      name={field}
                      value={formData[field] || ''}
                      onChange={handleChange}
                      className="w-full pl-10 pr-3 py-3 rounded-lg border focus:outline-none transition-all text-base"
                      style={{ 
                        borderColor: errors[field] ? colors.error : colors.border,
                        color: colors.text,
                        boxShadow: errors[field] 
                          ? `0 0 0 1px ${colors.error}` 
                          : `0 1px 2px rgba(0, 0, 0, 0.05)`,
                        backgroundColor: errors[field] ? `${colors.error}05` : "#FFFFFF",
                        transition: "all 0.3s ease"
                      }}
                      placeholder={
                        field === 'firstName' ? 'John' : 
                        field === 'lastName' ? 'Doe' :
                        field === 'email' ? 'your.email@example.com' :
                        '0123456789'
                      }
                    />
                    {errors[field] && (
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                        <X size={16} style={{ color: colors.error }} />
                      </div>
                    )}
                  </div>
                  {errors[field] && (
                    <p className="mt-2 text-xs" style={{ color: colors.error }}>{errors[field]}</p>
                  )}
                </div>
              ))}
              {isSuccess && (
              <div className="mb-6 p-4 rounded-lg flex items-center" 
                   style={{ backgroundColor: `${colors.success}10`, border: `1px solid ${colors.success}30` }}>
                <Check size={18} style={{ color: colors.success }} className="mr-3 flex-shrink-0" />
                <p className="text-sm font-medium" style={{ color: colors.success }}>
                  Your profile has been successfully updated
                </p>
              </div>
            )}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-3 mt-6 rounded-lg font-medium text-base transition-all duration-300 bg-violet-200 text-[#6f48e5] hover:bg-violet-100 focus:outline-none"
                style={{ 
                
                  boxShadow: `0 4px 12px ${colors.primary}30`,
                  opacity: isSubmitting ? 0.8 : 1,
                  letterSpacing: "0.01em",
                  transform: isSubmitting ? "scale(0.98)" : "scale(1)",
                  transition: "all 0.3s ease"
                }}
              >
                {isSubmitting ? 'Updating...' : 'Update Details'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default EnhancedUserForm;